import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CampaignSummaryField } from '@fmnts/api/next';
import {
  AbstractDeclarativeFieldComponent,
  TemplateBuilder,
  ensureTemplate,
  interpolateTemplate,
} from '@fmnts/declarative-forms';
import { DateService, TranslationService } from '@fmnts/i18n';
import { MomentModule } from 'ngx-moment';
import { Observable, defer, map, of } from 'rxjs';
import { SharedDeclarativeCampaignFormsModule } from '../../shared-declarative-next-forms.module';

@Component({
  selector: 'next-date-summary-field',
  templateUrl: './date-summary-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SharedDeclarativeCampaignFormsModule, MomentModule],
})
export class DateSummaryFieldComponent extends AbstractDeclarativeFieldComponent<CampaignSummaryField> {
  private readonly dateService = inject(DateService);
  protected readonly translationService = inject(TranslationService);
  protected readonly templateBuilder = inject(TemplateBuilder);

  /**
   * The format to use to display the date
   */
  public viewDateFormat = 'L';

  /** Observable that emits with the content */
  protected readonly content$: Observable<moment.Moment> = defer(() =>
    this.getDate$(this.field),
  ).pipe(
    this.dateService.moment$(),
    map((asMoment) => (asMoment.isValid() ? asMoment : undefined)),
  );

  /**
   * @param field
   *
   * @returns
   * An observable that emits with the date
   */
  private getDate$(field: CampaignSummaryField): Observable<unknown> {
    const { defaultValue } = field.widgetOptions;

    return this.translationService
      .fromInlineTranslation$(of(defaultValue))
      .pipe(
        // Ensure that for any emitted value that references a field
        // that it is transformed into templating form of `{{some-field-key}}`
        map((s) => ensureTemplate(s, this.declaration)),
        interpolateTemplate((part) => this.templateBuilder.interpolate(part)),
      );
  }
}
